/**
 * The swap button on the tile of another person on the web call stage: the
 * keyboard way to change the order of the tiles (tileOrder.ts). It sits in
 * the top-right corner, just left of the fit button (PictureFitButton.web.tsx):
 * a 28 px circle in a 44 px target. Its menu lists the tiles of the other
 * people. It shows and hides with the call bar, and it shows while it has
 * keyboard focus or its menu is open.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { SELF_VIEW_CONTROL_SIZE } from './SelfViewDeviceSettings.web';
const SWAP_BUTTON_SIZE = 28;
/**
 * The menu item that an arrow key, Home or End moves the focus to. The arrow
 * keys wrap around.
 *
 * @param {number} index - The item with the focus.
 * @param {number} count - The number of items.
 * @param {string} key - The key.
 * @returns {number|undefined} No number for a different key.
 */
export function nextMenuIndex(index, count, key) {
    if (count < 1) {
        return undefined;
    }
    switch (key) {
        case 'ArrowDown':
            return (index + 1) % count;
        case 'ArrowUp':
            return (index - 1 + count) % count;
        case 'Home':
            return 0;
        case 'End':
            return count - 1;
    }
    return undefined;
}
const useStyles = makeStyles()(theme => ({
    // No box of its own: the button and the menu sit on the picture.
    root: {
        display: 'contents'
    },
    button: {
        alignItems: 'center',
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        display: 'flex',
        height: SELF_VIEW_CONTROL_SIZE,
        justifyContent: 'center',
        padding: 0,
        position: 'absolute',
        right: SELF_VIEW_CONTROL_SIZE,
        top: 0,
        transition: 'opacity 220ms ease',
        width: SELF_VIEW_CONTROL_SIZE,
        zIndex: 5,
        '&:focus-visible': {
            opacity: 1,
            outline: 0,
            pointerEvents: 'auto'
        },
        '&:focus-visible > span': {
            outline: `2px solid ${theme.palette.focus01}`,
            outlineOffset: 2
        },
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none'
        }
    },
    hidden: {
        opacity: 0,
        pointerEvents: 'none'
    },
    circle: {
        alignItems: 'center',
        background: theme.palette.ui01,
        border: `1px solid ${theme.palette.ui04}`,
        borderRadius: '50%',
        boxSizing: 'border-box',
        display: 'flex',
        height: SWAP_BUTTON_SIZE,
        justifyContent: 'center',
        width: SWAP_BUTTON_SIZE
    },
    // Inside the picture: a small tile scrolls its menu.
    menu: {
        background: theme.palette.ui01,
        border: `1px solid ${theme.palette.ui04}`,
        borderRadius: 12,
        boxShadow: '0 14px 36px rgba(0, 0, 0, 0.32)',
        boxSizing: 'border-box',
        display: 'grid',
        gap: 2,
        maxHeight: `calc(100% - ${SELF_VIEW_CONTROL_SIZE + 8}px)`,
        overflowY: 'auto',
        padding: 4,
        position: 'absolute',
        right: 8,
        top: SELF_VIEW_CONTROL_SIZE,
        width: 'min(260px, calc(100% - 16px))',
        zIndex: 6
    },
    item: {
        background: 'transparent',
        border: 0,
        borderRadius: 8,
        color: theme.palette.text01,
        cursor: 'pointer',
        font: 'inherit',
        fontSize: 14,
        minHeight: SELF_VIEW_CONTROL_SIZE,
        overflow: 'hidden',
        padding: '0 12px',
        textAlign: 'start',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&:hover': {
            background: theme.palette.ui03
        },
        '&:focus-visible': {
            background: theme.palette.ui03,
            outline: `2px solid ${theme.palette.focus01}`,
            outlineOffset: -2
        }
    }
}));
const TileSwapMenu = ({ label, onSwap, targets, tileKey, visible }) => {
    const { classes, cx, theme } = useStyles();
    const [open, setOpen] = useState(false);
    const root = useRef(null);
    const button = useRef(null);
    const menu = useRef(null);
    // The item to focus when the menu opens, and whether the button gets
    // the focus back after the menu closes.
    const focusItem = useRef(undefined);
    const focusButton = useRef(false);
    const menuId = `vrs-swap-menu-${tileKey}`;
    // The items of the open menu, in the order shown.
    const items = () => Array.from(menu.current?.querySelectorAll('[role="menuitem"]') ?? []);
    const openAt = useCallback((index) => {
        if (open) {
            items()[index]?.focus();
            return;
        }
        focusItem.current = index;
        setOpen(true);
    }, [open]);
    const close = useCallback((refocus) => {
        focusButton.current = refocus;
        setOpen(false);
    }, []);
    // After the render: the first item of a menu that opened gets the focus,
    // or the button after the menu closed. A swap moves the tile in the page,
    // and the move takes the focus from the button.
    useEffect(() => {
        if (open && focusItem.current !== undefined) {
            items()[focusItem.current]?.focus();
            focusItem.current = undefined;
        }
        else if (!open && focusButton.current) {
            focusButton.current = false;
            button.current?.focus();
        }
    });
    // A press outside closes the menu.
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onPointerDown = (event) => {
            const target = event.target;
            if (!target || !root.current?.contains(target)) {
                setOpen(false);
            }
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [open]);
    const onButtonClick = useCallback(() => {
        open ? close(false) : openAt(0);
    }, [close, open, openAt]);
    // Tab and a click on a different control move the focus out: the menu closes.
    const onBlur = useCallback((event) => {
        const next = event.relatedTarget;
        if (!next || !root.current?.contains(next)) {
            setOpen(false);
        }
    }, []);
    const onKeyDown = useCallback((event) => {
        const index = items().indexOf(event.target);
        if (event.key === 'Escape') {
            if (open) {
                event.preventDefault();
                event.stopPropagation();
                close(true);
            }
            return;
        }
        // On the button: an arrow key opens the menu.
        if (index < 0) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                openAt(event.key === 'ArrowDown' ? 0 : targets.length - 1);
            }
            return;
        }
        const next = nextMenuIndex(index, targets.length, event.key);
        if (next !== undefined) {
            event.preventDefault();
            items()[next]?.focus();
        }
    }, [close, open, openAt, targets.length]);
    // A choice swaps the two tiles. The item names its tile.
    const onItemClick = useCallback((event) => {
        const key = event.currentTarget.getAttribute('data-swap-key');
        if (key) {
            onSwap(tileKey, key);
            close(true);
        }
    }, [close, onSwap, tileKey]);
    return (React.createElement("div", { className: classes.root, "data-vrs-no-drag": true, onBlur: onBlur, onKeyDown: onKeyDown, ref: root },
        React.createElement("button", { "aria-controls": open ? menuId : undefined, "aria-expanded": open, "aria-haspopup": 'menu', "aria-label": label, className: cx(classes.button, !visible && !open && classes.hidden), onClick: onButtonClick, ref: button, title: label, type: 'button' },
            React.createElement("span", { className: classes.circle },
                React.createElement("svg", { "aria-hidden": 'true', focusable: 'false', height: 16, viewBox: '0 0 24 24', width: 16 },
                    React.createElement("path", { d: 'M4 8h15M15 4l4 4-4 4M20 16H5M9 12l-4 4 4 4', fill: 'none', stroke: theme.palette.icon01, strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2 })))),
        open && (React.createElement("div", { "aria-label": label, className: classes.menu, id: menuId, ref: menu, role: 'menu' }, targets.map(target => (React.createElement("button", { className: classes.item, "data-swap-key": target.key, key: target.key, onClick: onItemClick, role: 'menuitem', tabIndex: -1, type: 'button' }, target.label)))))));
};
export default TileSwapMenu;
